const STORAGE_PREFIX = 'onuia-shipping-protection';
const STORAGE_TTL_MS = 4 * 60 * 60 * 1000;

const getConfig = () => {
  const configEl = document.querySelector('[data-shipping-protection-config]');
  if (!configEl) {
    return null;
  }

  try {
    return JSON.parse(configEl.textContent);
  } catch {
    return null;
  }
};

const getStorageKey = (variantId) => `${STORAGE_PREFIX}-${variantId}`;

const getStoredPreference = (variantId) => {
  try {
    const raw = localStorage.getItem(getStorageKey(variantId));
    if (!raw) {
      return null;
    }

    const data = JSON.parse(raw);
    if (Date.now() > data.expires) {
      localStorage.removeItem(getStorageKey(variantId));
      return null;
    }

    return data.active;
  } catch {
    return null;
  }
};

const setStoredPreference = (variantId, active) => {
  localStorage.setItem(
    getStorageKey(variantId),
    JSON.stringify({
      active,
      expires: Date.now() + STORAGE_TTL_MS,
    })
  );
};

const getSectionsToBundle = () => {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    })
  );
  return sections.join(',');
};

const fetchCart = async () => {
  const response = await fetch(`${Shopify.routes.root}cart.js`);
  return response.json();
};

const dispatchCartChange = (cartContent, baseEvent = 'shipping-protection:change') => {
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        cart: cartContent,
      },
    })
  );
};

const getCartParts = (cart, variantId) => {
  const protectionItem = cart.items.find((item) => item.variant_id === variantId);
  const realItems = cart.items.filter((item) => item.variant_id !== variantId);

  return {
    protectionItem,
    realItems,
    hasRealItems: realItems.length > 0,
    visibleItemCount: realItems.reduce((total, item) => total + item.quantity, 0),
  };
};

const getDesiredActive = (config, hasRealItems) => {
  const stored = getStoredPreference(config.variantId);
  if (stored !== null) {
    return stored;
  }

  if (!hasRealItems) {
    return false;
  }

  return config.activeByDefault;
};

const updateDrawerEmptyState = (visibleItemCount) => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) {
    return;
  }

  cartDrawer.classList.toggle('drawer--center-body', visibleItemCount === 0);

  for (const cartCount of document.querySelectorAll('cart-drawer .cart-count, .header__cart-count cart-count')) {
    cartCount.textContent = visibleItemCount;
  }
};

class CartShippingProtectionManager {
  #config = null;
  #isSyncing = false;
  #abortController = null;
  #userTogglePending = false;
  #loadingTimeoutId = null;

  init() {
    this.#config = getConfig();
    if (!this.#config?.variantId) {
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('cart:change', () => this.#syncCart(), { signal });
    document.addEventListener('cart:refresh', () => this.#syncCart(), { signal });
    document.addEventListener('cart-drawer:refreshed', () => this.#onCartDrawerRefreshed(), { signal });
    document.addEventListener('click', (event) => this.#onToggleClick(event), { signal });

    this.#bindToggle();
    this.#syncCart();
  }

  destroy() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #bindToggle() {
    for (const element of document.querySelectorAll('cart-shipping-protection')) {
      element.classList.toggle(
        'cart-shipping-protection--active',
        element.querySelector('[data-shipping-protection-toggle]')?.getAttribute('aria-pressed') === 'true'
      );
    }
  }

  #onCartDrawerRefreshed() {
    this.#bindToggle();

    if (this.#userTogglePending) {
      this.#setLoading(false);
      this.#userTogglePending = false;
    }
  }

  #setLoading(isLoading) {
    if (this.#loadingTimeoutId) {
      clearTimeout(this.#loadingTimeoutId);
      this.#loadingTimeoutId = null;
    }

    for (const element of document.querySelectorAll('cart-shipping-protection')) {
      element.classList.toggle('cart-shipping-protection--loading', isLoading);
      element.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      const toggle = element.querySelector('[data-shipping-protection-toggle]');
      toggle?.toggleAttribute('disabled', isLoading);
    }

    if (isLoading) {
      this.#loadingTimeoutId = setTimeout(() => {
        this.#setLoading(false);
        this.#userTogglePending = false;
      }, 12000);
    }
  }

  #updateComponentUI(isActive, lineKey = null) {
    for (const element of document.querySelectorAll('cart-shipping-protection')) {
      element.classList.toggle('cart-shipping-protection--active', isActive);

      const toggle = element.querySelector('[data-shipping-protection-toggle]');
      toggle?.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      if (lineKey) {
        element.dataset.lineKey = lineKey;
      } else if (!isActive) {
        delete element.dataset.lineKey;
      }
    }
  }

  async #onToggleClick(event) {
    const toggle = event.target.closest('[data-shipping-protection-toggle]');
    const element = toggle?.closest('cart-shipping-protection');

    if (!toggle || !element || this.#isSyncing || !this.#config?.variantId) {
      return;
    }

    event.preventDefault();

    const variantId = Number(this.#config.variantId);
    const isActive = toggle.getAttribute('aria-pressed') === 'true';
    const nextActive = !isActive;

    this.#setLoading(true);
    this.#userTogglePending = true;
    setStoredPreference(variantId, nextActive);
    this.#updateComponentUI(nextActive, isActive ? element.dataset.lineKey : null);

    this.#isSyncing = true;
    let success = false;

    try {
      if (nextActive) {
        success = await this.#addToCart();
      } else {
        let lineKey = element.dataset.lineKey;

        if (!lineKey) {
          const cart = await fetchCart();
          lineKey = getCartParts(cart, variantId).protectionItem?.key;
        }

        if (lineKey) {
          success = await this.#removeFromCart(lineKey);
        }
      }

      if (!success) {
        this.#updateComponentUI(!nextActive);
        setStoredPreference(variantId, !nextActive);
        this.#setLoading(false);
        this.#userTogglePending = false;
      }
    } catch {
      this.#updateComponentUI(!nextActive);
      setStoredPreference(variantId, !nextActive);
      this.#setLoading(false);
      this.#userTogglePending = false;
    } finally {
      this.#isSyncing = false;
    }
  }

  async #syncCart() {
    if (this.#isSyncing || !this.#config?.variantId) {
      return;
    }

    const variantId = Number(this.#config.variantId);
    const cart = await fetchCart();
    const { protectionItem, hasRealItems, visibleItemCount } = getCartParts(cart, variantId);
    const desiredActive = getDesiredActive(this.#config, hasRealItems);

    updateDrawerEmptyState(visibleItemCount);

    if (!hasRealItems && protectionItem) {
      this.#isSyncing = true;
      try {
        await this.#removeFromCart(protectionItem.key);
      } finally {
        this.#isSyncing = false;
      }
      return;
    }

    if (hasRealItems && desiredActive && !protectionItem) {
      this.#isSyncing = true;
      try {
        await this.#addToCart();
      } finally {
        this.#isSyncing = false;
      }
      return;
    }

    if (hasRealItems && !desiredActive && protectionItem) {
      this.#isSyncing = true;
      try {
        await this.#removeFromCart(protectionItem.key);
      } finally {
        this.#isSyncing = false;
      }
      return;
    }

    this.#updateComponentUI(Boolean(protectionItem), protectionItem?.key ?? null);
  }

  async #addToCart() {
    const sections = getSectionsToBundle();
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(this.#config.variantId), quantity: 1 }],
        sections,
      }),
    });

    if (!response.ok) {
      return false;
    }

    const responseJson = await response.json();
    const cartContent = await fetchCart();
    cartContent.sections = responseJson.sections;

    const variantId = Number(this.#config.variantId);
    const { visibleItemCount, protectionItem } = getCartParts(cartContent, variantId);
    updateDrawerEmptyState(visibleItemCount);
    this.#updateComponentUI(true, protectionItem?.key ?? null);

    dispatchCartChange(cartContent);
    return true;
  }

  async #removeFromCart(lineKey) {
    const sections = getSectionsToBundle();
    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections,
      }),
    });

    if (!response.ok) {
      return false;
    }

    const cartContent = await response.json();
    const variantId = Number(this.#config.variantId);
    const { visibleItemCount } = getCartParts(cartContent, variantId);
    updateDrawerEmptyState(visibleItemCount);
    this.#updateComponentUI(false);

    dispatchCartChange(cartContent);
    return true;
  }
}

const manager = new CartShippingProtectionManager();

const initShippingProtection = () => {
  manager.destroy();
  manager.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingProtection);
} else {
  initShippingProtection();
}

document.addEventListener('shopify:section:load', initShippingProtection);
